import {
  applySyncV2Batch,
  applySyncV2SnapshotPages,
  readServerEvents,
  type SyncV2State,
  type SyncV2Batch,
  type TerrainEditRefusal,
} from '@arbestra/contracts';

export class SyncV2HttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterMs = 1000,
    /** Whether the server actually sent a Retry-After header. */
    readonly hasRetryAfter = false,
    /** The refused gesture of a terrain batch (0-based), when the server names it. */
    readonly gesture: number | null = null,
    /** The refused units of a terrain batch where none was accepted (well-formed entries only). */
    readonly refused: readonly TerrainEditRefusal[] = [],
  ) {
    super(message);
  }
}
/** A refused synchronization request, typed from its JSON body and Retry-After. */
export function syncV2HttpError(response: Response, data: unknown): SyncV2HttpError {
  const error = (data ?? {}) as { code?: string; message?: string; gesture?: unknown; refused?: unknown };
  const header = response.headers.get('retry-after');
  const seconds = Number(header ?? '1');
  return new SyncV2HttpError(
    response.status,
    error.code ?? 'HTTP_ERROR',
    error.message ?? 'Synchronisation indisponible.',
    Number.isFinite(seconds) ? Math.max(0, Math.min(seconds, 60)) * 1000 : 1000,
    header !== null && Number.isFinite(seconds),
    Number.isSafeInteger(error.gesture) && (error.gesture as number) >= 0 ? (error.gesture as number) : null,
    terrainEditRefusals(error.refused),
  );
}
const isCell = (c: unknown): c is { cellX: number; cellY: number } =>
  typeof c === 'object' &&
  c !== null &&
  Number.isSafeInteger((c as { cellX?: unknown }).cellX) &&
  Number.isSafeInteger((c as { cellY?: unknown }).cellY);
/** The well-formed refused units of a terrain edits answer (`result.refused`
 * or the 409 body); anything else is dropped, never trusted blindly. */
export function terrainEditRefusals(value: unknown): TerrainEditRefusal[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((r: unknown) => {
    const entry = r as Partial<Record<keyof TerrainEditRefusal, unknown>> | null;
    return entry &&
      Number.isSafeInteger(entry.gesture) &&
      (entry.gesture as number) >= 0 &&
      typeof entry.code === 'string' &&
      isCell(entry.cell) &&
      isCell(entry.at)
      ? [
          {
            gesture: entry.gesture as number,
            code: entry.code as TerrainEditRefusal['code'],
            cell: { cellX: entry.cell.cellX, cellY: entry.cell.cellY },
            at: { cellX: entry.at.cellX, cellY: entry.at.cellY },
          },
        ]
      : [];
  });
}

export interface AppliedSyncCursor {
  kind: string;
  key: string;
  epoch: string;
  revision: string;
}
export class SyncV2ResyncRequired extends Error {
  constructor() {
    super('SYNC_V2_RESYNC_REQUIRED');
  }
}
export class SyncV2SessionExpired extends Error {
  constructor() {
    super('SYNC_V2_SESSION_EXPIRED');
  }
}

/** One subscription generation owns its socket and canonical cursors. HTTP
 * acknowledgements may update overlays, but reconnect always uses applied
 * complete state. The game adapter supplies the bootstrap and publication. */
export class SyncV2Session {
  private state: SyncV2State;
  private controller: AbortController | null = null;
  private closed = false;
  constructor(
    initial: SyncV2State,
    private readonly publish: (state: SyncV2State, observedAt: string, batch: SyncV2Batch) => void,
    private readonly request: typeof fetch = (input, init) => fetch(input, init),
  ) {
    this.state = initial;
  }

  get snapshot(): SyncV2State {
    return this.state;
  }
  get cursors(): AppliedSyncCursor[] {
    return [...this.state.scopes].map(([scope, value]) => {
      const separator = scope.indexOf(':');
      return {
        kind: scope.slice(0, separator),
        key: scope.slice(separator + 1),
        epoch: value.epoch,
        revision: value.revision,
      };
    });
  }
  apply(input: unknown): 'applied' | 'ignored' {
    if (this.closed) return 'ignored';
    const result = applySyncV2Batch(this.state, input);
    if (result.kind === 'resync') throw new SyncV2ResyncRequired();
    if (result.kind === 'applied') {
      this.state = result.state;
      this.publish(this.state, (input as SyncV2Batch).observedAt, input as SyncV2Batch);
    }
    return result.kind;
  }
  /** A paged snapshot and its catch-up delta, published as one state; the
   * pages alone never are. `restart`: read it again from its first page.
   * Publishers receive a bare `scope-snapshot` marker (no group): a
   * snapshot was installed, which rereads the presentation. */
  applyPaged(pages: readonly unknown[], delta: unknown): 'applied' | 'restart' | 'ignored' {
    if (this.closed) return 'ignored';
    const result = applySyncV2SnapshotPages(this.state, pages, delta);
    if (result.kind === 'resync') throw new SyncV2ResyncRequired();
    if (result.kind === 'restart') return 'restart';
    this.state = result.state;
    const batch = delta as SyncV2Batch;
    this.publish(this.state, batch.observedAt, { ...batch, coverage: 'scope-snapshot', groups: [] });
    return 'applied';
  }
  close(): void {
    this.closed = true;
    this.controller?.abort();
    this.controller = null;
  }

  /** A single attempt. Reconnect scheduling belongs to the lifecycle owner;
   * overlapping attempts abort the previous reader before opening another. */
  async connect(url: string): Promise<void> {
    if (this.closed) return;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const current = () => !this.closed && this.controller === controller && !controller.signal.aborted;
    try {
      const response = await this.request(url, {
        method: 'POST',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: { 'content-type': 'application/json', accept: 'text/event-stream', 'x-arbestra-protocol': '2' },
        body: JSON.stringify({
          protocol: 2,
          generation: this.state.generation,
          scopes: this.cursors.map(({ kind, key }) => ({ kind, key })),
          cursors: this.cursors,
        }),
      });
      if (!current()) {
        await response.body?.cancel();
        return;
      }
      if (response.status === 401) throw new SyncV2SessionExpired();
      // A refused opening (a too large village, 503 with Retry-After) is typed
      // like any other read, so the transport spaces its retries.
      if (!response.ok) throw syncV2HttpError(response, await response.json().catch(() => null));
      if (!response.headers.get('content-type')?.startsWith('text/event-stream') || !response.body)
        throw new Error('SYNC_V2_INVALID_STREAM');
      await readServerEvents(response.body, {
        signal: controller.signal,
        current,
        onEvent: (event) => {
          if (event.event === 'heartbeat') return;
          if (event.event === 'expired') throw new SyncV2SessionExpired();
          if (event.event === 'reset') throw new SyncV2ResyncRequired();
          if (event.event !== 'batch') throw new Error('SYNC_V2_UNKNOWN_EVENT');
          this.apply(JSON.parse(event.data));
        },
      });
    } catch (error) {
      if (current()) throw error;
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }
}
